"use client";

import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { useForm } from "react-hook-form";
import { z } from "zod";

const imageUrlSchema = z.object({
  url: z.string().url("Please enter a valid URL"),
});

type ImageUrlFormData = z.infer<typeof imageUrlSchema>;

interface ImageUploaderProps {
  onImageSelected: (imageUrl: string | File) => void;
}

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB limit
const MAX_FILE_SIZE_FORMATTED = "3MB";

export function ImageUploader({ onImageSelected }: ImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();

  const {
    register,
    setValue,
    formState: { errors },
  } = useForm<ImageUrlFormData>({
    resolver: zodResolver(imageUrlSchema),
  });

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0]?.code === "file-too-large") {
          toast({
            variant: "destructive",
            title: "File too large",
            description: `Maximum file size is ${MAX_FILE_SIZE_FORMATTED}. Please choose a smaller image.`,
          });
          return;
        }
        toast({
          variant: "destructive",
          title: "Invalid file",
          description: "Please upload a valid image file (JPEG, PNG, or WebP).",
        });
        return;
      }

      if (acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];
      onImageSelected(file);
    },
    [onImageSelected, toast],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".webp"],
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    onDropAccepted: () => setIsDragging(false),
    onDropRejected: () => setIsDragging(false),
  });

  const extractImageUrl = (url: string): string => {
    try {
      // Check if it's a Next.js image URL or similar with a url parameter
      const urlObj = new URL(url);
      const encodedUrl = urlObj.searchParams.get("url");
      if (encodedUrl) {
        // Decode the URL parameter
        return decodeURIComponent(encodedUrl);
      }
      return url;
    } catch (e) {
      return url;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setValue("url", url);

    // Only trigger image selection if it's a valid image URL
    if (url.match(/^https?:\/\/.*[^?#].*\.(jpe?g|png|webp)/i)) {
      const extractedUrl = extractImageUrl(url);
      if (extractedUrl !== url) {
        setValue("url", extractedUrl);
      }
      onImageSelected(extractedUrl);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          if (file.size > MAX_FILE_SIZE) {
            toast({
              variant: "destructive",
              title: "File too large",
              description: `Maximum file size is ${MAX_FILE_SIZE_FORMATTED}. Please choose a smaller image.`,
            });
            return;
          }
          onImageSelected(file);
          return;
        }
      } else if (item.type === "text/plain") {
        item.getAsString((text) => {
          if (text.match(/^https?:\/\/.*[^?#].*\.(jpe?g|png|webp)/i)) {
            const extractedUrl = extractImageUrl(text);
            setValue("url", extractedUrl);
            onImageSelected(extractedUrl);
          }
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Drag & Drop Zone */}
      <div
        {...getRootProps()}
        className={cn(
          "group relative cursor-pointer overflow-hidden rounded-xl border-2 border-dashed transition-all duration-300",
          isDragging
            ? "border-canary bg-canary/5 shadow-lg"
            : "border-gray-200 hover:border-canary hover:bg-canary/5 hover:shadow-lg",
          "aspect-video px-4 sm:px-6",
        )}
      >
        <input {...getInputProps()} />

        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 opacity-50" />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 sm:gap-4 p-4 sm:p-6">
          <div
            className={cn(
              "rounded-full p-4 transition-all duration-300",
              isDragging
                ? "bg-canary/20 scale-110"
                : "bg-gray-100 group-hover:bg-canary/20 group-hover:scale-110",
            )}
          >
            <Upload
              className={cn(
                "h-8 w-8 transition-colors",
                isDragging
                  ? "text-canary"
                  : "text-gray-400 group-hover:text-canary",
              )}
            />
          </div>

          <div className="text-center">
            <p
              className={cn(
                "text-lg font-semibold transition-colors duration-300",
                isDragging
                  ? "text-canary"
                  : "text-gray-700 group-hover:text-canary",
              )}
            >
              Drop your image here
            </p>
            <p className="mt-2 text-sm text-gray-500">or paste an image/URL</p>
            <p className="mt-1 text-xs text-gray-400">
              Supports JPEG, PNG, WebP up to 4MB
            </p>
          </div>
        </div>
      </div>

      {/* URL Input */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or</span>
        </div>
      </div>

      <div className="space-y-2">
        <Input
          type="url"
          placeholder="Paste image URL here..."
          {...register("url")}
          onChange={handleUrlChange}
          onPaste={handlePaste}
          className="bg-white/90 border-gray-200/90 placeholder:text-gray-500/90 h-12 px-4 text-base focus-within:ring-canary"
        />
        {errors.url && (
          <p className="text-sm text-red-500">{errors.url.message}</p>
        )}
      </div>
    </div>
  );
}
